import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, ChevronsDownUp, ChevronsUpDown, Search, X } from 'lucide-react';
import {
  ADMIN_NAV,
  filterNavForModule,
  filterNavForGlobalModule,
  searchNav,
  type NavItem,
  type NavSection as NavSectionType,
} from '@/navigation/adminNav';
import {
  USERS_MODULE_NAV,
  filterUsersNav,
  isUsersModulePath,
} from '@/navigation/usersNav';
import { findActiveModule } from '@/navigation/modules';
import { useSidebarOrderCounts, type SidebarOrderCounts } from '@/hooks/useSidebarCounts';
import { useCurrentUser } from '@/hooks/useAuth';
import { NavIcon } from '@/layouts/NavIcon';
import { UserMenu } from '@/layouts/UserMenu';

type AdminSidebarProps = {
  moduleType?: string;
  collapsed: boolean;
  isMobile: boolean;
  mobileOpen: boolean;
  searchFocusNonce: number;
  expandedGroups: Record<string, boolean>;
  onToggleCollapsed: () => void;
  onToggleGroup: (key: string) => void;
  onEnsureGroupOpen: (key: string) => void;
  onSetAllGroups: (keys: string[], open: boolean) => void;
  onNavigate: () => void;
  onCloseMobile: () => void;
  onOpenSearch?: () => void;
};

function searchToKey(search?: Record<string, unknown>) {
  if (!search) return '';
  return Object.entries(search)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&');
}

function locationSearchToKey(search: Record<string, unknown>) {
  return Object.entries(search)
    .filter(([key]) => key === 'status' || key === 'type')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&');
}

function itemIsActive(item: NavItem, pathname: string, search: Record<string, unknown>, searchKey: string) {
  const exact = pathname === item.to;
  const nested = pathname.startsWith(`${item.to}/`);
  if (!exact && !nested) return false;
  if (!item.search) return nested || !searchKey;
  if (nested) return true;
  return Object.entries(item.search).every(([key, value]) => {
    const actual = search[key];
    if (key === 'status' && String(value) === 'all' && (actual === undefined || actual === '' || actual === null)) return true;
    return String(actual ?? '') === String(value);
  });
}

function itemOrChildIsActive(item: NavItem, pathname: string, search: Record<string, unknown>, searchKey: string): boolean {
  return itemIsActive(item, pathname, search, searchKey)
    || (item.children ?? []).some((child) => itemOrChildIsActive(child, pathname, search, searchKey));
}

function sectionIsActive(section: NavSectionType, pathname: string, search: Record<string, unknown>, searchKey: string) {
  return section.items.some((item) => itemOrChildIsActive(item, pathname, search, searchKey));
}

function countFor(item: NavItem, counts: SidebarOrderCounts | undefined): number | undefined {
  if (!item.countKey || !counts) return undefined;
  return counts[item.countKey as keyof SidebarOrderCounts];
}

const groupKey = (sectionId: string, label: string) => `group:${sectionId}:${label}`;
const sectionKey = (sectionId: string) => `section:${sectionId}`;

type TooltipPlacement = 'bottom' | 'right';

type TooltipState = {
  top: number;
  left: number;
  placement: TooltipPlacement;
};

const TOOLTIP_SHOW_DELAY = 150;
const TOOLTIP_HIDE_DELAY = 80;

/**
 * Portal tooltip anchored to a nav/header control.
 * Renders outside the sidebar so `overflow: hidden` cannot clip it.
 * Title-only content; no descriptions, paths, or badges.
 */
function useSidebarTooltip(label: string, enabled: boolean, placement: TooltipPlacement = 'right') {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (showTimer.current !== null) window.clearTimeout(showTimer.current);
    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    showTimer.current = null;
    hideTimer.current = null;
  }, []);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTooltip({
      placement,
      top: placement === 'bottom' ? rect.bottom + 8 : rect.top + rect.height / 2,
      left: placement === 'bottom' ? rect.left + rect.width / 2 : rect.right + 10,
    });
  }, [placement]);

  const show = useCallback(() => {
    if (!enabled) return;
    clearTimers();
    showTimer.current = window.setTimeout(place, TOOLTIP_SHOW_DELAY);
  }, [clearTimers, enabled, place]);

  const hide = useCallback(() => {
    clearTimers();
    hideTimer.current = window.setTimeout(() => setTooltip(null), TOOLTIP_HIDE_DELAY);
  }, [clearTimers]);

  useEffect(() => {
    clearTimers();
    setTooltip(null);
  }, [enabled, label, placement, clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  const tooltipNode =
    enabled && tooltip
      ? createPortal(
          <div
            role="tooltip"
            className={`sidebar-tooltip placement-${tooltip.placement}`}
            style={{ top: tooltip.top, left: tooltip.left }}
          >
            {label}
          </div>,
          document.body,
        )
      : null;

  return {
    anchorRef,
    tooltipNode,
    handlers: {
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: hide,
    },
  };
}

/** Collapse/expand control for the sidebar header. */
function SidebarCollapseToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  const { anchorRef, tooltipNode, handlers } = useSidebarTooltip(label, true, collapsed ? 'right' : 'bottom');

  return (
    <>
      <button
        ref={(node) => {
          anchorRef.current = node;
        }}
        type="button"
        className="sidebar-collapse-button"
        onClick={onToggle}
        aria-label={label}
        {...handlers}
      >
        {collapsed ? <ChevronRight size={18} aria-hidden /> : <ChevronLeft size={18} aria-hidden />}
      </button>
      {tooltipNode}
    </>
  );
}

function allGroupKeys(sections: NavSectionType[]) {
  return sections.flatMap((section) => [
    sectionKey(section.id),
    ...section.items.filter((item) => item.children?.length).map((item) => groupKey(section.id, item.label)),
  ]);
}

export const AdminSidebar = forwardRef<HTMLElement, AdminSidebarProps>(function AdminSidebar(
  {
    moduleType,
    collapsed,
    isMobile,
    mobileOpen,
    searchFocusNonce,
    expandedGroups,
    onToggleCollapsed,
    onToggleGroup,
    onEnsureGroupOpen,
    onSetAllGroups,
    onNavigate,
    onCloseMobile,
    onOpenSearch,
  },
  ref,
) {
  const location = useLocation();
  const pathname = location.pathname;
  const search = (location.search ?? {}) as Record<string, unknown>;
  const searchKey = locationSearchToKey(search);
  const [query, setQuery] = useState('');
  const [searchPopoverOpen, setSearchPopoverOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const countsQuery = useSidebarOrderCounts();
  const counts = countsQuery.data?.data;
  const { data: currentUser } = useCurrentUser();
  const effectiveCollapsed = isMobile ? false : collapsed;
  const usersModule = isUsersModulePath(pathname);
  const granted = useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.is_super_admin || currentUser.role_id === 1) return new Set(['all', '*']);
    return new Set(currentUser.permissions ?? []);
  }, [currentUser]);
  const nav = useMemo(() => {
    if (usersModule) {
      return searchNav(filterUsersNav(USERS_MODULE_NAV, granted) as NavSectionType[], query);
    }
    const globalModule = findActiveModule(pathname);
    const scoped = filterNavForGlobalModule(ADMIN_NAV, globalModule?.id);
    return searchNav(filterNavForModule(scoped, moduleType, granted), query);
  }, [granted, moduleType, pathname, query, usersModule]);

  useEffect(() => {
    if (query.trim()) return;
    nav.forEach((section) => {
      if (sectionIsActive(section, pathname, search, searchKey)) onEnsureGroupOpen(sectionKey(section.id));
      section.items.forEach((item) => {
        if (item.children?.length && itemOrChildIsActive(item, pathname, search, searchKey)) {
          onEnsureGroupOpen(groupKey(section.id, item.label));
        }
      });
    });
  }, [nav, onEnsureGroupOpen, pathname, query, searchKey]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT';
      const openSearchHotkey =
        (event.key === '/' && !event.metaKey && !event.ctrlKey && !typing) ||
        ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k');
      if (!openSearchHotkey) return;
      event.preventDefault();
      // Expand collapsed sidebar / mobile drawer first so the search field is visible.
      onOpenSearch?.();
      setSearchPopoverOpen(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onOpenSearch]);

  // Navbar search / hotkeys / collapsed icon → focus once the input is mounted.
  useEffect(() => {
    if (searchFocusNonce === 0) return;
    setSearchPopoverOpen(true);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      // Second frame: input is mounted after expand/drawer state lands in the DOM.
      raf2 = requestAnimationFrame(() => searchRef.current?.focus());
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [searchFocusNonce]);

  // Collapsed search popover: focus when the input mounts.
  useEffect(() => {
    if (!searchPopoverOpen || !effectiveCollapsed) return;
    const raf = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [searchPopoverOpen, effectiveCollapsed]);

  // Mobile drawer open after a search request → focus inline search field.
  useEffect(() => {
    if (!isMobile || !mobileOpen || searchFocusNonce === 0) return;
    const raf = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isMobile, mobileOpen, searchFocusNonce]);

  const totalLinks = nav.reduce((total, section) => total + section.items.reduce((n, item) => n + 1 + (item.children?.length ?? 0), 0), 0);
  const groupKeys = allGroupKeys(
    usersModule
      ? (filterUsersNav(USERS_MODULE_NAV, granted) as NavSectionType[])
      : filterNavForModule(
          filterNavForGlobalModule(ADMIN_NAV, findActiveModule(pathname)?.id),
          moduleType,
          granted,
        ),
  );
  const searchInput = (
    <div className="sidebar-search-input-wrap">
      <Search size={16} aria-hidden />
      <input
        ref={searchRef}
        type="search"
        value={query}
        placeholder="Search menu…  ( / )"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setQuery('');
            setSearchPopoverOpen(false);
          }
        }}
        aria-label="Search menu"
      />
      {query ? <button type="button" className="search-clear" onClick={() => setQuery('')} aria-label="Clear menu search"><X size={15} aria-hidden /></button> : null}
    </div>
  );

  return (
    <aside ref={ref} className="admin-sidebar" aria-label="Primary navigation" aria-hidden={isMobile && !mobileOpen ? true : undefined}>
      <div className="admin-brand">
        <div className="admin-brand-brand">
          <Link to={'/dashboard' as never} onClick={onNavigate} aria-label="Ezzo Admin home">
            <span className="brand-mark">E</span>
            <span className="brand-text">Ezzo Admin</span>
          </Link>
        </div>
        {isMobile ? (
          <button type="button" className="sidebar-drawer-close" data-sidebar-close onClick={onCloseMobile} aria-label="Close navigation"><X size={20} aria-hidden /></button>
        ) : (
          <SidebarCollapseToggle collapsed={effectiveCollapsed} onToggle={onToggleCollapsed} />
        )}
      </div>

      <div className={`sidebar-search ${effectiveCollapsed ? 'is-collapsed' : ''}`}>
        {effectiveCollapsed ? (
          <>
            <button type="button" className="sidebar-search-icon" aria-label="Search menu" title="Search menu" onClick={() => { setSearchPopoverOpen(true); requestAnimationFrame(() => requestAnimationFrame(() => searchRef.current?.focus())); }}><Search size={18} aria-hidden /></button>
            {searchPopoverOpen ? <div className="sidebar-search-popover">{searchInput}</div> : null}
          </>
        ) : searchInput}
      </div>

      {!effectiveCollapsed ? (() => {
        const allOpen = groupKeys.length > 0 && groupKeys.every((k) => expandedGroups[k] !== false);
        const allClosed = groupKeys.length > 0 && groupKeys.every((k) => expandedGroups[k] === false);
        return (
          <div className="sidebar-tools" role="toolbar" aria-label="Navigation section controls">
            <button
              type="button"
              className="sidebar-tool-btn"
              aria-label="Expand all navigation sections"
              aria-disabled={allOpen || undefined}
              title="Expand all sections"
              onClick={() => onSetAllGroups(groupKeys, true)}
            >
              <ChevronsDownUp size={16} aria-hidden />
            </button>
            <button
              type="button"
              className="sidebar-tool-btn"
              aria-label="Collapse all navigation sections"
              aria-disabled={allClosed || undefined}
              title="Collapse all sections"
              onClick={() => onSetAllGroups(groupKeys, false)}
            >
              <ChevronsUpDown size={16} aria-hidden />
            </button>
            <span className="sidebar-count">{totalLinks} links</span>
          </div>
        );
      })() : null}

      <nav className="admin-nav" aria-label="Admin navigation">
        {nav.map((section) => {
          const key = sectionKey(section.id);
          const open = query.trim() ? true : expandedGroups[key] !== false;
          const active = sectionIsActive(section, pathname, search, searchKey);
          return (
            <SidebarNavSection
              key={section.id}
              section={section}
              open={open}
              active={active}
              collapsed={effectiveCollapsed}
              pathname={pathname}
              search={search}
              searchKey={searchKey}
              counts={counts}
              expandedGroups={expandedGroups}
              searching={!!query.trim()}
              onToggleGroup={onToggleGroup}
              onNavigate={onNavigate}
            />
          );
        })}
        {nav.length === 0 ? <div className="nav-empty">No menu matches “{query}”</div> : null}
      </nav>

      <div className="sidebar-user"><UserMenu collapsed={effectiveCollapsed} /></div>
    </aside>
  );
});

function SidebarNavSection({ section, open, active, collapsed, pathname, search, searchKey, counts, expandedGroups, searching, onToggleGroup, onNavigate }: {
  section: NavSectionType;
  open: boolean;
  active: boolean;
  collapsed: boolean;
  pathname: string;
  search: Record<string, unknown>;
  searchKey: string;
  counts: SidebarOrderCounts | undefined;
  expandedGroups: Record<string, boolean>;
  searching: boolean;
  onToggleGroup: (key: string) => void;
  onNavigate: () => void;
}) {
  const key = sectionKey(section.id);
  const { anchorRef, tooltipNode, handlers } = useSidebarTooltip(section.title, collapsed);
  return (
    <section className={`nav-section ${active ? 'has-active' : ''} ${open ? 'open' : ''}`}>
      <button
        ref={(node) => {
          anchorRef.current = node;
        }}
        type="button"
        className="nav-section-toggle"
        onClick={() => onToggleGroup(key)}
        aria-expanded={open}
        aria-controls={`nav-section-${section.id}`}
        aria-label={section.title}
        {...(collapsed ? handlers : {})}
      >
        <span className="nav-section-icon"><NavIcon name={section.icon} size={collapsed ? 24 : 20} /></span>
        <span className="nav-section-title">{section.title}</span>
        <span className="nav-section-count">{section.items.reduce((n, item) => n + 1 + (item.children?.length ?? 0), 0)}</span>
        <span className={`chevron ${open ? 'up' : 'down'}`} aria-hidden>▾</span>
      </button>
      {tooltipNode}
      {open ? (
        <ul id={`nav-section-${section.id}`} className="nav-list">
          {section.items.map((item) => <NavRow key={`${item.to}-${item.label}`} item={item} sectionId={section.id} pathname={pathname} search={search} searchKey={searchKey} counts={counts} expandedGroups={expandedGroups} collapsed={collapsed} searching={searching} onToggleGroup={onToggleGroup} onNavigate={onNavigate} />)}
        </ul>
      ) : null}
    </section>
  );
}

function NavRow({ item, sectionId, pathname, search, searchKey, counts, expandedGroups, collapsed, searching, onToggleGroup, onNavigate }: {
  item: NavItem;
  sectionId: string;
  pathname: string;
  search: Record<string, unknown>;
  searchKey: string;
  counts: SidebarOrderCounts | undefined;
  expandedGroups: Record<string, boolean>;
  collapsed: boolean;
  searching: boolean;
  onToggleGroup: (key: string) => void;
  onNavigate: () => void;
}) {
  const active = itemIsActive(item, pathname, search, searchKey);
  const hasChildren = !!item.children?.length;
  const key = groupKey(sectionId, item.label);
  const open = searching || expandedGroups[key] !== false;
  const childActive = (item.children ?? []).some((child) => itemOrChildIsActive(child, pathname, search, searchKey));
  const { anchorRef, tooltipNode, handlers } = useSidebarTooltip(item.label, collapsed);

  if (!hasChildren) {
    return (
      <li>
        <Link
          ref={(node: HTMLElement | null) => {
            anchorRef.current = node;
          }}
          to={item.to as never}
          search={item.search as never}
          className={`nav-link depth-${item.depth ?? 0} ${active ? 'active' : ''}`}
          activeProps={{ className: `nav-link depth-${item.depth ?? 0} active` }}
          activeOptions={{ exact: item.to === '/dashboard' }}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          aria-label={item.label}
          {...(collapsed ? handlers : {})}
        >
          {item.icon ? <span className="nav-item-icon"><NavIcon name={item.icon} size={collapsed ? 24 : 20} /></span> : <span className="nav-indicator" aria-hidden />}
          <span className="nav-link-label">{item.label}</span><CountBadge value={countFor(item, counts)} tone={item.badgeTone} />
        </Link>
        {tooltipNode}
      </li>
    );
  }

  const controlId = `nav-group-${sectionId}-${item.label.replace(/\W+/g, '-')}`;
  return (
    <li className={`nav-group ${childActive || active ? 'has-active' : ''}`}>
      <button
        ref={(node) => {
          anchorRef.current = node;
        }}
        type="button"
        className={`nav-group-toggle ${childActive || active ? 'active' : ''}`}
        onClick={() => onToggleGroup(key)}
        aria-expanded={open}
        aria-controls={controlId}
        aria-label={item.label}
        {...(collapsed ? handlers : {})}
      >
        {item.icon ? <span className="nav-item-icon"><NavIcon name={item.icon} size={collapsed ? 24 : 20} /></span> : <span className="nav-indicator" aria-hidden />}
        <span className="nav-link-label">{item.label}</span><span className={`chevron ${open ? 'up' : 'down'}`} aria-hidden>▾</span>
      </button>
      {tooltipNode}
      {open ? <ul id={controlId} className="nav-list nav-sublist">{item.children?.map((child) => <NavRow key={`${child.to}-${child.label}-${searchToKey(child.search)}`} item={child} sectionId={sectionId} pathname={pathname} search={search} searchKey={searchKey} counts={counts} expandedGroups={expandedGroups} collapsed={collapsed} searching={searching} onToggleGroup={onToggleGroup} onNavigate={onNavigate} />)}</ul> : null}
    </li>
  );
}

function CountBadge({ value, tone = 'info' }: { value?: number; tone?: NavItem['badgeTone'] }) {
  if (value === undefined) return null;
  return <span className={`nav-badge tone-${tone ?? 'info'}`}>{value}</span>;
}
